import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/ui/Header';
import Footer from '../components/ui/Footer';
import { Badge } from '../components/ui/badge';
import { Download, FileVideo, Monitor, Mail, Inbox, Sparkles, Package, Check } from 'lucide-react';

const SundayEditKit = () => {
  useEffect(() => {
    window.scrollTo(0, 0);

    // Load the form embed script
    const script = document.createElement('script');
    script.src = 'https://book.phosstudio.net/js/form_embed.js';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      const existingScript = document.querySelector('script[src="https://book.phosstudio.net/js/form_embed.js"]');
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, []);

  const includedItems = [
    { icon: <Package className="w-5 h-5" />, text: '10+ Drag and drop motion presets' },
    { icon: <FileVideo className="w-5 h-5" />, text: '.prfpset & .mogrt file formats included' },
    { icon: <Monitor className="w-5 h-5" />, text: 'Compatible with Adobe Premiere Pro 2022+' },
    { icon: <Download className="w-5 h-5" />, text: 'Instant digital download — delivered to your inbox' },
  ];

  const steps = [
    { number: '1', title: 'Enter your info', description: 'Drop your name and email in the form below.', icon: <Mail className="w-6 h-6" /> },
    { number: '2', title: 'Check your inbox', description: 'You\'ll get an instant email with the download link.', icon: <Inbox className="w-6 h-6" /> },
    { number: '3', title: 'Start editing', description: 'Drop the presets into Premiere Pro and transform your footage.', icon: <Sparkles className="w-6 h-6" /> },
  ];

  return (
    <div className="min-h-screen relative z-[1]">
      <Header />
      <div className="pt-32 pb-20">

        {/* ===== HERO SECTION ===== */}
        <section className="py-12 relative">
          <div className="container mx-auto px-6">
            <div className="max-w-3xl mx-auto text-center">
              <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/50 mb-4">Free Download</Badge>
              <h1 className="text-4xl sm:text-5xl md:text-6xl font-black text-white mb-6">
                The Sunday <span className="bg-gradient-to-r from-amber-400 to-yellow-300 bg-clip-text text-transparent">Edit Kit</span>
              </h1>
              <p className="text-lg sm:text-xl text-slate-400 max-w-2xl mx-auto mb-6">
                A curated preset pack for Premiere Pro by Elijah Edwards. Drop your info below and get instant access.
              </p>
            </div>
          </div>
        </section>

        {/* ===== FORM SECTION (UNTOUCHED) ===== */}
        <section className="py-12 relative">
          <div className="container mx-auto px-6">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-8">
                <h2 className="text-2xl sm:text-3xl font-black text-white">
                  Grab Your <span className="bg-gradient-to-r from-amber-400 to-yellow-300 bg-clip-text text-transparent">Free Kit</span>
                </h2>
              </div>

              {/* Signup Form Embed — DO NOT MODIFY */}
              <div className="bg-slate-900/50 border border-amber-500/30 backdrop-blur-sm rounded-2xl shadow-2xl shadow-amber-500/10 overflow-hidden">
                <iframe
                  src="https://book.phosstudio.net/widget/form/9uXD0KL3TyZDerVEJrx8"
                  style={{ width: '100%', height: '635px', border: 'none', borderRadius: '3px' }}
                  id="inline-9uXD0KL3TyZDerVEJrx8"
                  data-layout="{'id':'INLINE'}"
                  data-trigger-type="alwaysShow"
                  data-trigger-value=""
                  data-activation-type="alwaysActivated"
                  data-activation-value=""
                  data-deactivation-type="neverDeactivate"
                  data-deactivation-value=""
                  data-form-name="Newsletter Form"
                  data-height="undefined"
                  data-layout-iframe-id="inline-9uXD0KL3TyZDerVEJrx8"
                  data-form-id="9uXD0KL3TyZDerVEJrx8"
                  title="Sunday Edit Kit Signup"
                />
              </div>

              {/* Trust note below form */}
              <div className="mt-6 text-center">
                <p className="text-slate-500 text-xs sm:text-sm">
                  No spam. Unsubscribe anytime. By signing up you agree to our{' '}
                  <Link to="/privacy" className="text-amber-400/70 hover:text-amber-400 underline transition-colors">Privacy Policy</Link>
                  {' '}and{' '}
                  <Link to="/terms" className="text-amber-400/70 hover:text-amber-400 underline transition-colors">Terms of Service</Link>.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ===== WHAT'S INCLUDED SECTION ===== */}
        <section className="py-16 relative">
          <div className="container mx-auto px-6">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-10">
                <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-white mb-4">
                  What You{`'`}re <span className="bg-gradient-to-r from-amber-400 to-yellow-300 bg-clip-text text-transparent">Getting</span>
                </h2>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {includedItems.map((item, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-4 bg-slate-900/50 border border-slate-800 rounded-xl p-5 hover:border-amber-500/30 transition-all"
                  >
                    <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center text-amber-400">
                      {item.icon}
                    </div>
                    <p className="text-slate-300 font-medium text-sm sm:text-base leading-relaxed">{item.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ===== HOW IT WORKS SECTION ===== */}
        <section className="py-16 relative">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-white mb-4">
                  How It <span className="bg-gradient-to-r from-amber-400 to-yellow-300 bg-clip-text text-transparent">Works</span>
                </h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {steps.map((step, index) => (
                  <div key={index} className="relative text-center group">
                    {/* Connector line (desktop only) */}
                    {index < steps.length - 1 && (
                      <div className="hidden md:block absolute top-10 left-[60%] w-[80%] h-0.5 bg-gradient-to-r from-amber-500/40 to-amber-500/10 z-0" />
                    )}
                    <div className="relative z-10 bg-slate-900/50 border border-slate-800 rounded-2xl p-8 hover:border-amber-500/30 transition-all">
                      <div className="w-14 h-14 rounded-full bg-gradient-to-br from-amber-500 to-yellow-400 flex items-center justify-center mx-auto mb-5 shadow-lg shadow-amber-500/30">
                        <span className="text-slate-950 font-black text-xl">{step.number}</span>
                      </div>
                      <div className="w-12 h-12 rounded-lg bg-amber-500/10 flex items-center justify-center text-amber-400 mx-auto mb-4">
                        {step.icon}
                      </div>
                      <h3 className="text-white font-bold text-lg mb-2">{step.title}</h3>
                      <p className="text-slate-400 text-sm">{step.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

      </div>
      <Footer />
    </div>
  );
};

export default SundayEditKit;
